import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getManualServiceProviders } from '../services/subscriberWalletService';
import { addMedicineToCatalog } from '../services/medicineCatalogService';
import { getCurrentRole } from '../utils/medicineHelpers';

const AddMedicineForm = () => {
  const navigate = useNavigate();
  const currentRole = getCurrentRole();
  
  const [formData, setFormData] = useState({
    service_item_name: '',
    vendor_id: '',
    partner_price: '',
    partner_discount: '',
    service_item_id: ''
  });

  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const vendorsFetchedRef = useRef(false);

  // Fetch vendors on component mount (only once)
  useEffect(() => {
    const fetchVendors = async () => {
      if (!currentRole) return;
      
      // Prevent duplicate calls (especially in StrictMode)
      if (vendorsFetchedRef.current) return;
      vendorsFetchedRef.current = true;
      
      setLoadingVendors(true);
      try {
        const response = await getManualServiceProviders(currentRole, 'medicine');
        if (response && response.metadata && response.metadata.api_status) {
          setVendors(response.msg?.providers || []);
        } else {
          setErrorMsg('Failed to load service providers. Please refresh the page.');
          // Reset on error so it can retry if needed
          vendorsFetchedRef.current = false;
        }
      } catch (error) {
        setErrorMsg(`Error loading vendors: ${error.message}`);
        // Reset on error so it can retry if needed
        vendorsFetchedRef.current = false;
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, [currentRole]);

  // Validate discount against price
  const validateDiscount = useCallback((mrp, discount) => {
    const mrpNum = parseFloat(mrp);
    const discountNum = parseFloat(discount);
    
    if (!mrp || !discount) return null;
    if (isNaN(mrpNum) || isNaN(discountNum)) return null;
    if (discountNum > mrpNum) {
      return 'AyushPay MRP cannot exceed MRP';
    }
    return null;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      // Real-time validation for price/discount relationship
      if (name === 'partner_price' || name === 'partner_discount') {
        const discountError = validateDiscount(updated.partner_price, updated.partner_discount);
        
        setFieldErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          
          if (discountError) {
            newErrors.partner_discount = discountError;
          } else if (newErrors.partner_discount === 'AyushPay MRP cannot exceed MRP') {
            delete newErrors.partner_discount;
          }
          
          return newErrors;
        });
      }
      
      return updated;
    });
    
    // Clear field-specific errors when user starts typing
    setFieldErrors((prev) => {
      if (prev[name] && name !== 'partner_discount') {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
    
    // Clear general error message
    setErrorMsg((prev) => prev ? '' : prev);
  }, [validateDiscount]);

  // Pure validation function
  const validateForm = useCallback((data) => {
    const errors = {};
    const trimmedName = data.service_item_name.trim();

    // Validate medicine name
    if (!trimmedName) {
      errors.service_item_name = 'Medicine name is required';
    } else if (trimmedName.length > 255) {
      errors.service_item_name = 'Medicine name must be 255 characters or less';
    }

    // Validate vendor
    if (!data.vendor_id) {
      errors.vendor_id = 'Service provider is required';
    }

    // Validate MRP
    const mrp = parseFloat(data.partner_price);
    if (!data.partner_price || data.partner_price === '') {
      errors.partner_price = 'MRP is required';
    } else if (isNaN(mrp) || mrp < 0) {
      errors.partner_price = 'MRP must be a number greater than or equal to 0';
    }

    // Validate AyushPay MRP
    const discount = parseFloat(data.partner_discount);
    if (!data.partner_discount || data.partner_discount === '') {
      errors.partner_discount = 'AyushPay MRP is required';
    } else if (isNaN(discount) || discount < 0) {
      errors.partner_discount = 'AyushPay MRP must be a number greater than or equal to 0';
    } else if (!isNaN(mrp) && discount > mrp) {
      errors.partner_discount = 'AyushPay MRP cannot exceed MRP';
    }

    return errors;
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    setFieldErrors({});

    // Client-side validation
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      // Normalize service_item_name: trim and replace multiple spaces with single space
      const normalizedName = formData.service_item_name.trim().replace(/\s+/g, ' ');
      
      const payload = {
        service_item_name: normalizedName,
        vendor_id: formData.vendor_id,
        partner_price: Number(formData.partner_price) || 0,
        partner_discount: Number(formData.partner_discount) || 0
      };

      // Add optional service_item_id if provided
      const trimmedCustomId = formData.service_item_id.trim();
      if (trimmedCustomId) {
        payload.service_item_id = trimmedCustomId;
      }

      const response = await addMedicineToCatalog(currentRole, payload);

      if (response?.metadata?.api_status) {
        // Success
        const addedItem = response.msg?.item;
        setSuccessMsg(`Medicine "${addedItem?.service_item_name || formData.service_item_name}" added successfully!`);
        setFormData({
          service_item_name: '',
          vendor_id: '',
          partner_price: '',
          partner_discount: '',
          service_item_id: ''
        });
        setFieldErrors({});
        // Navigate back to medicine management after 2 seconds
        setTimeout(() => {
          navigate('/subscriber-wallet/add-medicine');
        }, 2000);
      } else {
        // Handle API error response
        const errorData = response?.msg?.error;
        
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          // Handle DRF serializer format
          if (errorData.non_field_errors) {
            const nonFieldErrorMsg = Array.isArray(errorData.non_field_errors)
              ? errorData.non_field_errors.join(', ')
              : String(errorData.non_field_errors);
            setErrorMsg(nonFieldErrorMsg);
          }
          
          // Field-level validation errors
          const fieldErrorsObj = {};
          Object.keys(errorData).forEach((field) => {
            if (field !== 'non_field_errors') {
              fieldErrorsObj[field] = Array.isArray(errorData[field]) 
                ? errorData[field].join(', ') 
                : String(errorData[field]);
            }
          });
          
          if (Object.keys(fieldErrorsObj).length > 0) {
            setFieldErrors(fieldErrorsObj);
            if (!errorData.non_field_errors) {
              setErrorMsg('Please fix the errors in the form.');
            }
          }
        } else {
          // General error message
          const errorMessage = typeof errorData === 'string' 
            ? errorData 
            : (Array.isArray(errorData) ? errorData.join(', ') : 'Failed to add medicine. Please try again.');
          setErrorMsg(errorMessage);
        }
      }
    } catch (error) {
      setErrorMsg(error.message || 'Failed to add medicine. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, currentRole, navigate]);

  const handleReset = useCallback(() => {
    setFormData({
      service_item_name: '',
      vendor_id: '',
      partner_price: '',
      partner_discount: '',
      service_item_id: ''
    });
    setSuccessMsg('');
    setErrorMsg('');
    setFieldErrors({});
  }, []);

  // Memoize validation errors for submit button state
  const validationErrors = useMemo(() => validateForm(formData), [formData, validateForm]);
  const hasClientErrors = Object.keys(validationErrors).length > 0;
  
  // Memoize form submission disabled state
  const isSubmitDisabled = useMemo(() => loading || loadingVendors || hasClientErrors, [loading, loadingVendors, hasClientErrors]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-2xl space-y-6">
        {/* Back buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/subscriber-wallet/home')} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to Home
            </button>
            <button 
              onClick={() => navigate('/subscriber-wallet/add-medicine')} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to Medicine Management
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Add Medicine to Service Catalog</h2>
            <p className="text-sm text-gray-600 mt-1">Fill in the details to add a new medicine item</p>
          </div>

          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">{successMsg}</p>
            </div>
          )}

          {errorMsg && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{errorMsg}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Service Item Name */}
            <div>
              <label htmlFor="service_item_name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Medicine Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="service_item_name"
                name="service_item_name"
                value={formData.service_item_name}
                onChange={handleChange}
                placeholder="Enter medicine name (e.g., Paracetamol 500mg)"
                required
                maxLength={255}
                className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  fieldErrors.service_item_name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {fieldErrors.service_item_name && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.service_item_name}</p>
              )}
            </div>

            {/* Service Provider/Vendor */}
            <div>
              <label htmlFor="vendor_id" className="block text-sm font-medium text-gray-700 mb-1.5">
                Service Provider <span className="text-red-500">*</span>
              </label>
              <select
                id="vendor_id"
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                required
                disabled={loadingVendors}
                className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  fieldErrors.vendor_id ? 'border-red-500' : 'border-gray-300'
                } ${loadingVendors ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">
                  {loadingVendors ? 'Loading providers...' : 'Select service provider'}
                </option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name || vendor.code || vendor.id}
                  </option>
                ))}
              </select>
              {fieldErrors.vendor_id && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.vendor_id}</p>
              )}
              {loadingVendors && (
                <p className="mt-1 text-xs text-gray-500">Loading service providers...</p>
              )}
            </div>

            {/* Partner Price (MRP) */}
            <div>
              <label htmlFor="partner_price" className="block text-sm font-medium text-gray-700 mb-1.5">
                MRP <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="partner_price"
                name="partner_price"
                value={formData.partner_price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
                className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  fieldErrors.partner_price ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {fieldErrors.partner_price && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.partner_price}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Enter the Maximum Retail Price (MRP)</p>
            </div>

            {/* Partner Discount (AyushPay MRP) */}
            <div>
              <label htmlFor="partner_discount" className="block text-sm font-medium text-gray-700 mb-1.5">
                AyushPay MRP <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="partner_discount"
                name="partner_discount"
                value={formData.partner_discount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
                className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                  fieldErrors.partner_discount ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {fieldErrors.partner_discount && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.partner_discount}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Enter the AyushPay price (discounted price). Must be ≤ MRP.</p>
            </div>

            {/* Service Item ID (Optional) */}
            <div>
              <label htmlFor="service_item_id" className="block text-sm font-medium text-gray-700 mb-1.5">
                Item ID <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="service_item_id"
                name="service_item_id"
                value={formData.service_item_id}
                onChange={handleChange}
                placeholder="Leave empty to auto-generate"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              />
              <p className="text-xs text-gray-500 mt-1">A unique ID will be auto-generated if not provided.</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="flex-1 bg-green-600 text-white px-6 py-2.5 rounded-md font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add Medicine'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="px-6 py-2.5 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMedicineForm;

